import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { toast } from 'react-toastify';
import {
  Camera,
  ClipboardList,
  Download,
  Eye,
  ImageOff,
  IndianRupee,
  Loader2,
  Package,
  Pencil,
  Plus,
  Ruler,
  Trash2,
} from 'lucide-react';
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  normalizeProductStockForUi,
} from "../store";
import { deleteProductImageFromImageKit } from '../firebase/storageHelpers';
import ProductForm from '../components/product_management/ProductForm';
import StockAdjustment from '../components/product_management/StockAdjustment';
import './ProductManagement.css';

const iconProps = { size: 18, strokeWidth: 1.75 };

const ProductManagement = () => {
  const dispatch = useDispatch();
  const productsFromStore = useSelector((state) => state.products.items || []);
  const canDeleteProducts = useSelector((state) => state.user?.role === 'admin');

  const [products, setProducts] = useState(productsFromStore);
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [deletingImageIndex, setDeletingImageIndex] = useState(null);

  const [filters, setFilters] = useState({
    category: '',
    stockStatus: '',
    search: ''
  });

  const [stats, setStats] = useState({
    total: 0,
    lowStock: 0,
    outOfStock: 0,
    totalValue: 0
  });

  // Fetch products on mount
  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  // Update products whenever productsFromStore changes
  useEffect(() => {
    setProducts(productsFromStore);
  }, [productsFromStore]);

  // Update stats whenever products change
  useEffect(() => {
    const newStats = {
      total: products.length,
      lowStock: products.filter(p => p.stock > 0 && p.stock < (p.moq || 100)).length,
      outOfStock: products.filter(p => p.stock === 0).length,
      totalValue: products.reduce((sum, p) => sum + (p.stock * (p.price?.amount || 0)), 0)
    };
    setStats(newStats);
  }, [products]);

  // Filter products based on current filters
  useEffect(() => {
    let filtered = products.filter(product => {
      const matchesCategory = !filters.category || product.category === filters.category;
      const matchesStockStatus = !filters.stockStatus ||
        (filters.stockStatus === 'in-stock' && product.stock > (product.moq || 100)) ||
        (filters.stockStatus === 'low-stock' && product.stock > 0 && product.stock <= (product.moq || 100)) ||
        (filters.stockStatus === 'out-of-stock' && product.stock === 0);
      const matchesSearch = !filters.search ||
        (product.name && product.name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(filters.search.toLowerCase())) ||
        (product.category && product.category.toLowerCase().includes(filters.search.toLowerCase()));

      return matchesCategory && matchesStockStatus && matchesSearch;
    });
    setFilteredProducts(filtered);
  }, [products, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const openAddProductModal = () => {
    setFormMode('add');
    setCurrentProduct(null);
    setIsFormModalOpen(true);
  };

  const openEditProductModal = (product) => {
    setFormMode('edit');
    setCurrentProduct(product);
    setIsFormModalOpen(true);
  };

  const openStockModal = (product) => {
    setCurrentProduct(product);
    setIsStockModalOpen(true);
  };

  const viewProduct = (product) => {
    setCurrentProduct(product);
    setIsViewModalOpen(true);
  };

  const closeAllModals = () => {
    setIsFormModalOpen(false);
    setIsStockModalOpen(false);
    setIsViewModalOpen(false);
    setCurrentProduct(null);
    setDeletingImageIndex(null);
  };

  const handleDeleteProduct = async (productId) => {
    const product = products.find(p => p._id === productId);
    if (!product) return;
    if (!canDeleteProducts) {
      toast.error('Only administrators can delete products.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
      try {
        await dispatch(deleteProduct(productId)).unwrap();
        dispatch(fetchProducts());
      } catch (error) {
        console.error('Failed to delete product:', error);
      }
    }
  };

  const handleProductSubmit = async (productData) => {
    try {
      if (formMode === 'add') {
        await dispatch(createProduct(productData)).unwrap();
      } else {
        await dispatch(updateProduct({ productId: currentProduct._id, data: productData })).unwrap();
      }
      dispatch(fetchProducts());
      closeAllModals();
    } catch (error) {
      console.error('Failed to save product:', error);
    }
  };

  const handleStockAdjustment = () => {
    dispatch(fetchProducts());
    closeAllModals();
  };

  const handleDeleteProductImage = async (imageIndex) => {
    if (!currentProduct?._id || deletingImageIndex !== null) return;
    const urls = currentProduct.images || [];
    const url = urls[imageIndex];
    if (url == null) return;

    const ok = window.confirm(
      'Remove this image from the product? It will no longer appear in the catalog or storefront.'
    );
    if (!ok) return;

    const nextImages = urls.filter((_, i) => i !== imageIndex);
    setDeletingImageIndex(imageIndex);
    try {
      try {
        await deleteProductImageFromImageKit(url);
      } catch (ikErr) {
        const d = ikErr?.response?.data;
        toast.warn(
          d?.details ||
            d?.error ||
            ikErr?.message ||
            'Could not delete the file from ImageKit; removing it from the product anyway.'
        );
      }

      const payload = await dispatch(
        updateProduct({
          productId: currentProduct._id,
          data: { images: nextImages },
        })
      ).unwrap();
      const raw = payload?.product;
      if (raw) {
        setCurrentProduct(normalizeProductStockForUi(raw));
      } else {
        setCurrentProduct((prev) =>
          prev ? { ...prev, images: nextImages } : prev
        );
      }
      dispatch(fetchProducts());
    } catch (error) {
      const data = error?.response?.data ?? error;
      const msg =
        data?.error?.message ||
        data?.message ||
        error?.message ||
        'Could not remove the image. Try again.';
      toast.error(msg);
    } finally {
      setDeletingImageIndex(null);
    }
  };

  const handleExport = () => {
    alert('Export functionality would be implemented here. Products data would be exported to CSV/Excel.');
  };

  const getStockBadgeClass = (stock, moq = 100) => {
    if (stock === 0) return 'pm-stock-out';
    if (stock <= moq) return 'pm-stock-low';
    return 'pm-stock-in';
  };

  const getStockBadgeText = (stock, moq = 100) => {
    if (stock === 0) return 'Out of Stock';
    if (stock <= moq) return 'Low Stock';
    return 'In Stock';
  };

  // Get unique categories for filter
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div className="pm-product-management">
      {/* Main Content */}
      <div className="pm-main-container">
        {/* Page Header */}
        <div className="pm-page-header">
          <h1 className="pm-page-title">Product Management</h1>
          <div className="pm-page-actions">
            <button type="button" className="pm-btn-secondary" onClick={handleExport}>
              <Download {...iconProps} className="pm-btn-leading-icon" aria-hidden />
              Export Products
            </button>
            <button type="button" className="pm-btn-primary" onClick={openAddProductModal}>
              <Plus {...iconProps} className="pm-btn-leading-icon" aria-hidden />
              Add New Product
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="pm-stats-grid">
          <div className="pm-stats-card">
            <div className="pm-stats-value">{stats.total}</div>
            <div className="pm-stats-label">Total Products</div>
          </div>
          <div className="pm-stats-card pm-stats-warning">
            <div className="pm-stats-value">{stats.lowStock}</div>
            <div className="pm-stats-label">Low Stock Items</div>
          </div>
          <div className="pm-stats-card pm-stats-danger">
            <div className="pm-stats-value">{stats.outOfStock}</div>
            <div className="pm-stats-label">Out of Stock</div>
          </div>
          <div className="pm-stats-card pm-stats-success">
            <div className="pm-stats-value">₹{stats.totalValue.toLocaleString()}</div>
            <div className="pm-stats-label">Total Inventory Value</div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="pm-filters-section">
          <div className="pm-filters-row">
            <div className="pm-filter-group">
              <label className="pm-filter-label">Category</label>
              <select
                className="pm-filter-select"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="pm-filter-group">
              <label className="pm-filter-label">Stock Status</label>
              <select
                className="pm-filter-select"
                value={filters.stockStatus}
                onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
              >
                <option value="">All Stock Levels</option>
                <option value="in-stock">In Stock</option>
                <option value="low-stock">Low Stock</option>
                <option value="out-of-stock">Out of Stock</option>
              </select>
            </div>
            <div className="pm-filter-group pm-search-group">
              <label className="pm-filter-label">Search Products</label>
              <input
                type="text"
                className="pm-search-filter"
                placeholder="Product name, category..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="pm-products-table-container">
          <div className="pm-table-header">
            <div className="pm-table-title">All Products</div>
            <div className="pm-table-count">Showing {filteredProducts.length.toLocaleString()} products</div>
          </div>

          <table className="pm-products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product Name</th>
                <th>Category</th>
                <th>Size</th>
                <th>Stock</th>
                <th>MOQ</th>
                <th>Price</th>
                <th>Tax %</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product._id} className="pm-table-row">
                  <td>
                    <div className="pm-product-image">
                      {product.images && product.images[0] ? (
                        <img src={product.images[0]} alt={product.name} />
                      ) : (
                        <div className="pm-no-image" aria-hidden>
                          <ImageOff {...iconProps} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="pm-product-info">
                      <div className="pm-product-name">{product.name}</div>
                      <div className="pm-product-description">{product.description}</div>
                    </div>
                  </td>
                  <td>{product.category}</td>
                  <td>
                    <div className="pm-size-info">
                      {product.bagSize?.cm}
                    </div>
                  </td>
                  <td>
                    <div className="pm-stock-quantity">{product.stock}</div>
                  </td>
                  <td>{product.moq || 100}</td>
                  <td>
                    <div className="pm-price">
                      {product.price?.currency} {product.price?.amount?.toFixed(2)}
                    </div>
                  </td>
                  <td>{`${Number(product.taxPercentage ?? 18)}%`}</td>
                  <td>
                    <span className={`pm-stock-badge ${getStockBadgeClass(product.stock, product.moq)}`}>
                      {getStockBadgeText(product.stock, product.moq)}
                    </span>
                  </td>
                  <td>
                    <div className="pm-action-buttons">
                      <button
                        type="button"
                        className="pm-btn-icon pm-btn-view"
                        onClick={() => viewProduct(product)}
                        title="View Product"
                        aria-label="View product"
                      >
                        <Eye {...iconProps} />
                      </button>
                      <button
                        type="button"
                        className="pm-btn-icon pm-btn-edit"
                        onClick={() => openEditProductModal(product)}
                        title="Edit Product"
                        aria-label="Edit product"
                      >
                        <Pencil {...iconProps} />
                      </button>
                      <button
                        type="button"
                        className="pm-btn-icon pm-btn-stock"
                        onClick={() => openStockModal(product)}
                        title="Adjust Stock"
                        aria-label="Adjust stock"
                      >
                        <Package {...iconProps} />
                      </button>
                      {canDeleteProducts && (
                        <button
                          type="button"
                          className="pm-btn-icon pm-btn-delete"
                          onClick={() => handleDeleteProduct(product._id)}
                          title="Delete Product"
                          aria-label="Delete product"
                        >
                          <Trash2 {...iconProps} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Form Modal (Add/Edit) */}
      {isFormModalOpen && (
        <ProductForm
          mode={formMode}
          product={currentProduct}
          onSubmit={handleProductSubmit}
          onClose={closeAllModals}
        />
      )}

      {/* Stock Adjustment Modal */}
      {isStockModalOpen && currentProduct && (
        <StockAdjustment
          product={currentProduct}
          onComplete={handleStockAdjustment}
          onClose={closeAllModals}
        />
      )}

      {/* Product View Modal */}
      {isViewModalOpen && currentProduct && (
        <div className="pm-modal-overlay" onClick={closeAllModals}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">Product Details</h2>
              <button className="pm-modal-close" onClick={closeAllModals}>&times;</button>
            </div>

            <div className="pm-modal-content">
              {/* Product Images */}
              <div className="pm-product-detail-section">
                <h3 className="pm-section-title">
                  <Camera {...iconProps} className="pm-section-title-icon" aria-hidden />
                  Product Images
                </h3>
                <div className="pm-image-gallery">
                  {currentProduct.images && currentProduct.images.length > 0 ? (
                    currentProduct.images.map((img, idx) => (
                      <div key={`${img}-${idx}`} className="pm-gallery-item">
                        <img
                          src={img}
                          alt={`${currentProduct.name} ${idx + 1}`}
                          className="pm-gallery-image"
                        />
                        <button
                          type="button"
                          className="pm-gallery-delete"
                          onClick={() => handleDeleteProductImage(idx)}
                          disabled={deletingImageIndex !== null}
                          title="Remove image"
                          aria-label={`Remove image ${idx + 1}`}
                        >
                          {deletingImageIndex === idx ? (
                            <Loader2 {...iconProps} className="pm-gallery-delete-spinner" aria-hidden />
                          ) : (
                            <Trash2 {...iconProps} aria-hidden />
                          )}
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="pm-no-images">No images available</div>
                  )}
                </div>
              </div>

              {/* Product Info Section */}
              <div className="pm-product-detail-section">
                <h3 className="pm-section-title">
                  <ClipboardList {...iconProps} className="pm-section-title-icon" aria-hidden />
                  Product Information
                </h3>
                <div className="pm-detail-grid">
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Product Name</div>
                    <div className="pm-detail-value">{currentProduct.name}</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Category</div>
                    <div className="pm-detail-value">{currentProduct.category}</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Material</div>
                    <div className="pm-detail-value">{currentProduct.material}</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Description</div>
                    <div className="pm-detail-value">{currentProduct.description}</div>
                  </div>
                </div>
              </div>

              {/* Size & Specifications */}
              <div className="pm-product-detail-section">
                <h3 className="pm-section-title">
                  <Ruler {...iconProps} className="pm-section-title-icon" aria-hidden />
                  Size & Specifications
                </h3>
                <div className="pm-detail-grid">
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Size (cm)</div>
                    <div className="pm-detail-value">{currentProduct.bagSize?.cm}</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Size (inch)</div>
                    <div className="pm-detail-value">{currentProduct.bagSize?.inch}</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">GSM</div>
                    <div className="pm-detail-value">{currentProduct.specifications?.gsm}</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">BF</div>
                    <div className="pm-detail-value">{currentProduct.specifications?.bf}</div>
                  </div>
                </div>
              </div>

              {/* Stock & Pricing */}
              <div className="pm-product-detail-section">
                <h3 className="pm-section-title">
                  <IndianRupee {...iconProps} className="pm-section-title-icon" aria-hidden />
                  Stock & Pricing
                </h3>
                <div className="pm-detail-grid">
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Current Stock</div>
                    <div className="pm-detail-value">
                      <span className={`pm-stock-badge ${getStockBadgeClass(currentProduct.stock, currentProduct.moq)}`}>
                        {currentProduct.stock} units
                      </span>
                    </div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Minimum Order Quantity</div>
                    <div className="pm-detail-value">{currentProduct.moq || 100} units</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Unit Price</div>
                    <div className="pm-detail-value">
                      {currentProduct.price?.currency} {currentProduct.price?.amount?.toFixed(2)}/{currentProduct.price?.unit}
                    </div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Tax (%)</div>
                    <div className="pm-detail-value">
                      {`${Number(currentProduct.taxPercentage ?? 18)}%`}
                    </div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Total Value</div>
                    <div className="pm-detail-value">
                      {currentProduct.price?.currency} {(currentProduct.stock * (currentProduct.price?.amount || 0)).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pm-modal-actions">
              <button className="pm-btn-cancel" onClick={closeAllModals}>Close</button>
              <button className="pm-btn-secondary" onClick={() => {
                closeAllModals();
                openStockModal(currentProduct);
              }}>Adjust Stock</button>
              <button className="pm-btn-primary" onClick={() => {
                closeAllModals();
                openEditProductModal(currentProduct);
              }}>Edit Product</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
